import React, { useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { DELETE_BY_CONDITION_OPS } from "../constants";
import {
  dryRunDeleteByCondition,
  executeDeleteByCondition,
} from "../services/deleteByConditionService";
import { writeAuditLog } from "../services/auditLogService";
import { trackDelete } from "../services/monitorService";
import {
  DeleteByConditionConfirmModal,
  DeleteProgressModal,
  DeleteResultModal,
} from "../components/DeleteByConditionModals";

export default function DeleteByConditionPage() {
  const { collectionName, selectCollection, allCollections, user, can, Permission } = useFirestoreAdmin();

  const [field, setField] = useState("");
  const [op, setOp] = useState("==");
  const [value, setValue] = useState("");
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [executing, setExecuting] = useState(false);

  const filter = { field: field.trim(), op, value };

  const runDryRun = async () => {
    if (!can(Permission.delete)) {
      toast.error("Delete permission required");
      return;
    }
    if (!collectionName.trim()) {
      toast.error("Select a collection");
      return;
    }
    if (!field.trim()) {
      toast.error("Field is required");
      return;
    }
    if (op === "in" && !value.trim()) {
      toast.error('Value is required (comma-separated for "in")');
      return;
    }

    setDryRunLoading(true);
    setPreview(null);
    setResult(null);
    try {
      const report = await dryRunDeleteByCondition(collectionName, filter);
      setPreview(report);
      if (report.affectedDocuments === 0) {
        toast("No documents match this condition");
      } else {
        toast.success(`${report.affectedDocuments.toLocaleString()} matching document(s)`);
      }
    } catch (err) {
      toast.error(err?.message || "Dry run failed — check field, operator, and Firestore indexes");
    } finally {
      setDryRunLoading(false);
    }
  };

  const executeDelete = async () => {
    if (!preview || preview.affectedDocuments === 0) return;

    setExecuting(true);
    setPreview(null);
    setProgress({ phase: "starting", message: "Preparing…" });

    try {
      const outcome = await executeDeleteByCondition({
        collectionName,
        filter,
        adminUid: user?.uid,
        adminName: user?.email,
        onProgress: setProgress,
      });

      trackDelete(outcome.deleted);

      await writeAuditLog({
        adminName: user?.email,
        adminUid: user?.uid,
        action: "delete_by_condition",
        collection: collectionName,
        documentIds: outcome.documentIds,
        oldValues: {
          filter,
          matchCount: outcome.matchCount,
          deleted: outcome.deleted,
          failed: outcome.failed,
          backupCount: outcome.backups?.length || 0,
          errors: outcome.errors,
        },
        newValues: null,
      });

      setProgress(null);
      setResult(outcome);
      if (outcome.failed > 0) {
        toast.error(`Delete finished: ${outcome.deleted} deleted, ${outcome.failed} failed`);
      } else {
        toast.success(`Deleted ${outcome.deleted} document(s)`);
      }
    } catch (err) {
      setProgress(null);
      toast.error(err?.message || "Delete failed");
    } finally {
      setExecuting(false);
    }
  };

  const closePreview = () => {
    if (!executing) setPreview(null);
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Delete by Condition</h1>
        <p className="text-sm text-slate-600">
          Delete documents matching a field filter. Dry run, backup, and confirmation are required before any
          deletion.
        </p>
      </header>

      <div className="fa-card space-y-4 p-4">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Collection</label>
          <select
            className="w-full max-w-md rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={collectionName}
            onChange={(e) => selectCollection(e.target.value)}
          >
            {allCollections.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Field</label>
            <input
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="e.g. device_name"
              value={field}
              onChange={(e) => setField(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Operator</label>
            <select
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={op}
              onChange={(e) => setOp(e.target.value)}
            >
              {DELETE_BY_CONDITION_OPS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Value</label>
            <input
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              placeholder={op === "in" ? "a, b, c (max 10)" : 'e.g. HIKEVISION'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700">
          {field.trim() ? (
            <span>
              {collectionName || "…"} where {field} {op}{" "}
              {op === "in" ? `(${value || "…"})` : `"${value || "…"}"`}
            </span>
          ) : (
            <span className="text-slate-500">Build a condition above</span>
          )}
        </div>

        <button
          type="button"
          className="fa-btn fa-btn-primary"
          onClick={runDryRun}
          disabled={dryRunLoading || !can(Permission.delete)}
        >
          {dryRunLoading ? "Running dry run…" : "Dry run"}
        </button>

        {!can(Permission.delete) && (
          <p className="text-xs text-amber-700">Your role does not have delete permission.</p>
        )}
      </div>

      <div className="fa-card border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-950">
        <p className="font-semibold">Safety workflow</p>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>Dry run — preview match count and sample IDs</li>
          <li>Confirmation — type DELETE to enable delete</li>
          <li>Backup — all matches saved to firestore_backups</li>
          <li>Delete — WriteBatch (500 per batch) with progress</li>
          <li>Audit — logged to admin_logs</li>
        </ol>
      </div>

      <DeleteByConditionConfirmModal
        preview={preview}
        collectionName={collectionName}
        filter={filter}
        onCancel={closePreview}
        onConfirm={executeDelete}
        confirming={executing}
      />
      <DeleteProgressModal progress={progress} />
      <DeleteResultModal result={result} onClose={() => setResult(null)} />
    </div>
  );
}
